import { Link } from 'react-router-dom';
import { useTokens, useRefreshToken, useDeleteToken } from '../../hooks/useTokens';
import { TokenCard } from './TokenCard';
import { Loader2, AlertCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import api from '../../services/api';
import type { Token } from '../../types';

export const TokenList = () => {
  const { data: tokens, isLoading, error } = useTokens();
  const refreshToken = useRefreshToken();
  const deleteToken = useDeleteToken();
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [reauthorizingProviderId, setReauthorizingProviderId] = useState<string | null>(null);

  const handleRefresh = async (tokenId: string) => {
    try {
      setProcessingIds((prev) => new Set(prev).add(tokenId));
      await refreshToken.mutateAsync(tokenId);
    } catch (error) {
      console.error('Failed to refresh token:', error);
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(tokenId);
        return next;
      });
    }
  };

  const handleDelete = async (tokenId: string) => {
    if (!confirm('Are you sure you want to delete this token?')) {
      return;
    }

    try {
      setProcessingIds((prev) => new Set(prev).add(tokenId));
      await deleteToken.mutateAsync(tokenId);
    } catch (error) {
      console.error('Failed to delete token:', error);
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(tokenId);
        return next;
      });
    }
  };

  // Deduplicate tokens per provider/external user, keep the newest by createdAt
  const visibleTokens = useMemo(() => {
    if (!tokens) return [];
    const map = new Map<string, Token>();
    for (const t of tokens) {
      const key = `${t.providerId}|${t.externalUserId ?? t.externalUserEmail ?? ''}`;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, t);
        continue;
      }
      const isNewer = new Date(t.createdAt).getTime() > new Date(existing.createdAt).getTime();
      if (isNewer) {
        map.set(key, t);
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [tokens]);

  const handleReauthorize = async (providerId: string) => {
    try {
      setReauthorizingProviderId(providerId);

      // Call API endpoint with JWT authentication to get authorization URL
      const response = await api.get<{ authorizationUrl: string }>(`/auth/authorize`, {
        params: { providerId }
      });

      // Redirect to provider's authorization page
      if (response.data?.authorizationUrl) {
        window.location.href = response.data.authorizationUrl;
      } else {
        throw new Error('No authorization URL received from API');
      }
    } catch (error) {
      console.error('Failed to initiate reauthorization:', error);
      alert('Failed to start reauthorization. Please try again.');
      setReauthorizingProviderId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Tokens</h3>
          <p className="text-gray-600">{(error as any).message || 'Failed to load tokens'}</p>
        </div>
      </div>
    );
  }

  if (!visibleTokens || visibleTokens.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Tokens Found</h3>
        <p className="text-gray-600 mb-4">
          You haven't connected any external accounts yet.
        </p>
        <Link to="/providers" className="btn btn-primary px-6 py-2">
          Connect Account
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Your Tokens</h2>
        <p className="text-gray-600 mt-1">
          Manage your connected external account tokens
        </p>
      </div>

      <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
        <p className="text-sm text-blue-900">
          Looking for token, provider, or template codes? Open <span className="font-semibold">View</span> on
          a token card and copy them from the details panel.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleTokens.map((token) => (
          <TokenCard
            key={token.id}
            token={token}
            onRefresh={handleRefresh}
            onDelete={handleDelete}
            onReauthorize={handleReauthorize}
            isRefreshing={refreshToken.isPending && processingIds.has(token.id)}
            isDeleting={deleteToken.isPending && processingIds.has(token.id)}
            isReauthorizing={reauthorizingProviderId === token.providerId}
          />
        ))}
      </div>
    </div>
  );
};
